import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  Paper,
  Stack,
  Alert,
} from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useMutation } from '@apollo/client';
import { GENERATE_FINGERPRINT } from '../graphql/generateFingerprint';
import { GET_ME } from '../graphql/getMe';

interface FingerprintConfirmationModalProps {
  open: boolean;
  onClose: () => void;
}

const FingerprintConfirmationModal: React.FC<
  FingerprintConfirmationModalProps
> = ({ open, onClose }) => {
  const [generateFingerprint, { loading: generatingFingerprint }] = useMutation(
    GENERATE_FINGERPRINT,
    {
      refetchQueries: [GET_ME],
    },
  );

  const handleConfirm = async () => {
    try {
      await generateFingerprint();
      onClose();
    } catch (err) {
      console.error('Error generating fingerprint:', err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Prevent closing
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      hideBackdrop={false}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          pb: 1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
          fontWeight: 600,
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <SecurityIcon sx={{ fontSize: '1.5rem' }} />
        Set Main Browser
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Main content with icon */}
          <Box sx={{ textAlign: 'center' }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                border: '1px solid rgba(102, 126, 234, 0.1)',
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: '3rem',
                  color: '#4caf50',
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#2c3e50',
                  mb: 1,
                }}
              >
                Is this your main study browser?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#5a6c7d',
                  lineHeight: 1.6,
                }}
              >
                Confirm this browser as your primary device for studying
              </Typography>
            </Paper>
          </Box>

          {/* Warning alert */}
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontSize: '0.9rem',
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Important Notice
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Once confirmed, you'll only be able to access your account from
              this browser. This is to prevent account sharing. If you have
              questions about it, please contact us at{' '}
              <Box
                component="a"
                href="mailto:boardexamapp@gmail.com"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 'inherit',
                  fontSize: 'inherit',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                boardexamapp@gmail.com
              </Box>
            </Typography>
          </Alert>

          {/* Action button */}
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={generatingFingerprint}
            fullWidth
            size="large"
            sx={{
              py: 1.5,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #b0b0b0 0%, #999999 100%)',
                boxShadow: 'none',
                transform: 'none',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {generatingFingerprint ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                <Typography>Setting up...</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon sx={{ fontSize: '1.2rem' }} />
                Yes, Set as Main Browser
              </Box>
            )}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default FingerprintConfirmationModal;
